<script setup lang="ts">
import dayjs from 'dayjs'

import MarketingFlowCard from './MarketingFlowCard.vue'
import MessagePreview from '@/components/MessagePreview.vue'
import type { ChipColor } from '@/components/base/BaseChip.vue'
import BaseChip from '@/components/base/BaseChip.vue'

import { getCustomLinks } from '@/components/ui/TextareaWithVars/utils'
import { getLocalizedWeekday, isNumber, isSameDay, sortActionsByPriority } from '../utils'

import type { Store } from '@/views/contests/Components/types/global'
import type {
  ActionSettings,
  DelaySettings,
  DelayUnit,
  MarketingFlowStep,
  TriggerSettings
} from '../types/marketingFlow'
import type { Audience, LoyaltyReward } from '@/types/types'
import { CLICK_TRIGGERED_ACTION_TYPES } from '../clickTrigger'
import { getMarketingFlowPreviewText } from '../marketingFlowMessage'

const props = defineProps<{
  triggerSettings: TriggerSettings
  audiences: Audience[]
  stores: Store[]
  rewards: LoyaltyReward[]
  delaySettings: DelaySettings
  actionSettings: ActionSettings
  disabledCardClick: {
    trigger: boolean
    delay: boolean
    action: boolean
  }
  showStats: boolean
  currentStep?: MarketingFlowStep | null
  isStatsLoaded?: boolean | null
  flowPerformance?: {
    triggeredCustomers: number
    completedCustomers: number
    delayedCustomers: number
  } | null
  stepHasError?: {
    trigger: boolean
    delay: boolean
    action: boolean
  }
  templateAudienceName?: string | null
}>()

const emit = defineEmits<{
  (e: 'triggerCardClicked'): void
  (e: 'delayCardClicked'): void
  (e: 'actionCardClicked'): void
}>()

const { t, locale } = useI18n()

const { isTriggerStepCompleted, isUsingTemplate, getMarketingFlowErrors } = useMarketingFlow()
const { segmentTypes } = useAudience()
const { loyaltyNotificationsConfigFields } = useLoyalty()

const marketingFlowErrors = computed(() => {
  return getMarketingFlowErrors(props.triggerSettings, props.delaySettings, props.actionSettings)
})
const hasRewardLinkWithoutReward = computed(() => {
  return marketingFlowErrors.value.some(
    (error) => error.type === 'action' && error.field === 'rewardAction' && error.i18nKey === 'requiredField'
  )
})

const triggerStepErrorStatus = computed(() => {
  if (
    props.templateAudienceName &&
    marketingFlowErrors.value.some(
      (error) => error.type === 'trigger' && error.field === 'selectedAudience' && error.i18nKey === 'requiredField'
    )
  ) {
    return null
  }
  if (marketingFlowErrors.value.some((error) => error.type === 'trigger' && error.severity === 'invalid')) {
    return 'invalid'
  }
  if (marketingFlowErrors.value.some((error) => error.type === 'trigger' && error.severity === 'incomplete')) {
    return 'incomplete'
  }
  return null
})
const delayStepErrorStatus = computed(() => {
  if (marketingFlowErrors.value.some((error) => error.type === 'delay' && error.severity === 'invalid')) {
    return 'invalid'
  }
  if (marketingFlowErrors.value.some((error) => error.type === 'delay' && error.severity === 'incomplete')) {
    return 'incomplete'
  }
  return null
})
const actionsStepErrorStatus = computed(() => {
  const hasRequiredActionsErrors = marketingFlowErrors.value.some(
    (error) => error.type === 'action' && error.field === 'actions' && error.i18nKey === 'requiredField'
  )
  if (hasRequiredActionsErrors && props.currentStep !== 'action') {
    return null
  }
  if (marketingFlowErrors.value.some((error) => error.type === 'action' && error.severity === 'invalid')) {
    return 'invalid'
  }
  if (marketingFlowErrors.value.some((error) => error.type === 'action' && error.severity === 'incomplete')) {
    return 'incomplete'
  }
  return null
})

const isTransactionalTrigger = computed(() => props.actionSettings.isTransactional)

const formatStats = (stats: number | null) => {
  if (props.showStats) {
    return stats ?? 0
  }
  return null
}

const selectedAudience = computed(() => {
  const currentAudience = props.audiences.find((audience) => audience.id === props.triggerSettings?.selectedAudience)

  return {
    ...currentAudience,
    ...segmentTypes.find((segType) => segType.id === currentAudience?.type)
  }
})

const selectedTriggerRewards = computed(() => {
  return (
    props.triggerSettings?.selectedRewards
      ?.filter((rewardId) => rewardId !== null)
      ?.map((rewardId) => props.rewards?.find((reward) => reward.id === rewardId))
      ?.filter((reward): reward is LoyaltyReward => reward !== undefined)
      ?.sort((a, b) => a.points - b.points) ?? []
  )
})

const selectedStores = computed(() => {
  return props.stores.filter((store) => props.triggerSettings?.selectedStores?.includes(store.id))
})
const selectedReward = computed(() => {
  return props.rewards?.find(
    (reward) =>
      reward.id === props.actionSettings.actions.find((action) => action.actionType === 'REWARD')?.selectedReward
  )
})

const pointsAction = computed(() => props.actionSettings.actions.find((action) => action.actionType === 'BONUS_POINTS'))
const multiplierAction = computed(() =>
  props.actionSettings.actions.find((action) => action.actionType === 'BONUS_MULTIPLIER')
)

const sortedActions = computed(() => sortActionsByPriority(props.actionSettings.actions))

const handleCardClick = (emitEvent: 'trigger' | 'delay' | 'action') => {
  if (!props.disabledCardClick[emitEvent]) {
    switch (emitEvent) {
      case 'trigger':
        emit('triggerCardClicked')
        break
      case 'delay':
        emit('delayCardClicked')
        break
      case 'action':
        emit('actionCardClicked')
        break
    }
  }
}

const getDelayUnitLabel = (unit: DelayUnit | null, count: number) => {
  if (!unit) {
    return ''
  }

  if (unit === 'DAYS') {
    return t('global.words.daysWithCount', {
      count: count
    })
  } else if (unit === 'HOURS') {
    return t('global.words.hoursWithCount', {
      count: count
    })
  } else if (unit === 'MINUTES') {
    return t('global.words.minutesWithCount', {
      count: count
    })
  }
}

const startDateFilter = computed(() => {
  return props.triggerSettings.filters?.find((filter) => filter.type === 'START_DATETIME')
})
const endDateTimeFilter = computed(() => {
  return props.triggerSettings.filters?.find((filter) => filter.type === 'END_DATETIME')
})
const weekdaysFilter = computed(() => {
  return props.triggerSettings.filters?.find((filter) => filter.type === 'WEEKDAYS')
})

const endTime = computed(() => {
  const timeFilter = props.triggerSettings.filters?.find((filter) => filter.type === 'TIME_RANGE')
  if (timeFilter && startDateFilter.value?.value) {
    const timeRangeParts = timeFilter.value?.split(' - ')
    if (timeRangeParts?.length === 2) {
      const endTimeStr = timeRangeParts[1]
      const startDate = new Date(startDateFilter.value.value)
      const baseDate = dayjs(startDate).format('YYYY-MM-DD')
      const endDateTime = new Date(`${baseDate}T${endTimeStr}`)

      return endDateTime.toISOString()
    }
    return props.triggerSettings.filters?.find((filter) => filter.type === 'END_DATETIME')?.value
  } else {
    return props.triggerSettings.filters?.find((filter) => filter.type === 'END_DATETIME')?.value
  }
})

const isFullDay = computed(() => {
  if (!startDateFilter.value?.value) {
    return false
  }

  const timeFilter = props.triggerSettings.filters?.find((filter) => filter.type === 'TIME_RANGE')
  if (timeFilter) {
    const timeRangeParts = timeFilter.value?.split(' - ')
    if (timeRangeParts?.length === 2) {
      const startTimeStr = timeRangeParts[0].substring(0, 5) // "00:00"
      const endTimeStr = timeRangeParts[1].substring(0, 5) // "23:59"
      return startTimeStr === '00:00' && endTimeStr === '23:59'
    }

    return false
  }

  const startMoment = dayjs(startDateFilter.value?.value)
  const isStartMidnight = startMoment.hour() === 0 && startMoment.minute() === 0 && startMoment.second() === 0

  const isRepeated =
    weekdaysFilter.value?.value ||
    (startDateFilter.value?.value &&
      endDateTimeFilter.value?.value &&
      !isSameDay(new Date(startDateFilter.value?.value), new Date(endDateTimeFilter.value?.value)))

  if (isRepeated) {
    return isStartMidnight
  }

  if (!endTime.value) {
    return false
  }

  const endMoment = dayjs(endTime.value)
  return isStartMidnight && endMoment.hour() === 23 && endMoment.minute() === 59 && endMoment.second() === 59
})

const formatOrdinal = (num: string | number): string => {
  const n = typeof num === 'string' ? parseInt(num) : num

  if (locale.value === 'en') {
    const j = n % 10
    const k = n % 100
    if (j === 1 && k !== 11) return 'st'
    if (j === 2 && k !== 12) return 'nd'
    if (j === 3 && k !== 13) return 'rd'
    return 'th'
  }

  return n === 1 ? 'er' : 'e'
}

const checkinCountLabel = computed(() => {
  const count =
    props.triggerSettings.checkinCount === 'custom'
      ? props.triggerSettings.customCheckinCount
      : props.triggerSettings.checkinCount

  if (props.triggerSettings.checkinCount === 'custom' && !count) {
    return t('pages.marketingFlow.marketingFlowEditPage.trigger.checkinCount.count', {
      count: `<span class="warning-tag">${t('pages.marketingFlow.marketingFlowEditPage.trigger.countToDefine')}</span>`
    })
  } else if (!isNumber(count)) {
    const invalidNumberText = t('pages.marketingFlow.marketingFlowEditPage.error.invalidNumberShort')
    return t('pages.marketingFlow.marketingFlowEditPage.trigger.checkinCount.count', {
      count: `<span class="error-tag">${invalidNumberText}</span>`
    })
  } else if (isNumber(count) && Number(count) > 0) {
    return t('pages.marketingFlow.marketingFlowEditPage.trigger.checkinCount.count', {
      count: Number(count),
      prefix: formatOrdinal(count)
    })
  }

  return t('pages.marketingFlow.marketingFlowEditPage.trigger.checkinCount.any')
})

const bonusMultiplierLabel = computed(() => {
  if (!multiplierAction.value?.bonusMultiplier) {
    return {
      label: t('pages.marketingFlow.marketingFlowEditPage.action.bonusMultiplierToDefine'),
      color: 'yellow' as ChipColor
    }
  }

  if (!isNumber(multiplierAction.value?.bonusMultiplier)) {
    return { label: t('pages.marketingFlow.marketingFlowEditPage.error.invalidNumberShort'), color: 'red' as ChipColor }
  }

  return {
    label: t('pages.marketingFlow.marketingFlowEditPage.action.multiplyBy', {
      multiplier: multiplierAction.value?.bonusMultiplier
    }),
    color: 'gray' as ChipColor
  }
})

const getExternalApiAuthTypeLabel = (authType?: string | null) => {
  if (!authType || authType === 'NONE')
    return t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authTypeNone')
  if (authType === 'BEARER') return t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authTypeBearer')
  if (authType === 'BASIC') return t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.authTypeBasic')
  return authType
}

const getExternalApiAuthSummary = (action: ActionSettings['actions'][number]) => ({
  typeLabel: getExternalApiAuthTypeLabel(action.externalApiAuthType),
  header: action.externalApiAuthPreview?.header
})

const hasExternalApiAuth = (action: ActionSettings['actions'][number]) =>
  Boolean(action.externalApiAuthType && action.externalApiAuthType !== 'NONE')

const getExternalApiAuthChipColor = (action: ActionSettings['actions'][number]): ChipColor =>
  action.isExternalApiAuthConfigSet ? 'green' : 'red'

const getExternalApiEndpointTitle = (action: ActionSettings['actions'][number]) => {
  const method =
    action.externalApiMethod || t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.methodPlaceholder')
  const url = action.externalApiUrl || t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.urlToDefine')
  return `${method} ${url}`
}

const getExternalApiEndpointLabel = (action: ActionSettings['actions'][number]) => {
  const method =
    action.externalApiMethod || t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.methodPlaceholder')
  const url = action.externalApiUrl?.trim()

  if (!url) {
    return `${method} ${t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.urlToDefine')}`
  }

  try {
    const { protocol, host } = new URL(url)
    return `${method} ${protocol}//${host}`
  } catch {
    return method
  }
}

const getExternalApiAuthDetailChips = (action: ActionSettings['actions'][number]) => {
  const summary = getExternalApiAuthSummary(action)
  const details: string[] = []

  if (summary.header) {
    details.push(
      `${t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.summaryHeader')}: ${summary.header}`
    )
  }

  return details
}

const labelCheckinFilterDate = computed(() => {
  let label = ''
  const isRepeated =
    startDateFilter.value?.value &&
    (weekdaysFilter?.value?.value ||
      !isSameDay(new Date(startDateFilter.value?.value), new Date(endDateTimeFilter?.value?.value)))

  if (!startDateFilter?.value?.value) {
    label = t('pages.marketingFlow.marketingFlowEditPage.trigger.whenCheckin', {
      checkinCount: checkinCountLabel.value
    })
  } else if (!isRepeated) {
    label = t('pages.marketingFlow.marketingFlowEditPage.trigger.whenCheckinDate', {
      checkinCount: checkinCountLabel.value,
      date: new Date(startDateFilter.value?.value).toLocaleDateString(locale.value, {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    })
  } else if (isRepeated && weekdaysFilter?.value?.value) {
    label = t('pages.marketingFlow.marketingFlowEditPage.trigger.whenCheckinWeekday', {
      checkinCount: checkinCountLabel.value,
      weekday: getLocalizedWeekday(weekdaysFilter.value.value, locale.value)
    })
  } else {
    label = t('pages.marketingFlow.marketingFlowEditPage.trigger.whenCheckin', {
      checkinCount: checkinCountLabel.value
    })
  }

  if (!isFullDay.value && startDateFilter.value?.value) {
    const timeFilter = props.triggerSettings.filters?.find((filter) => filter.type === 'TIME_RANGE')
    if (timeFilter) {
      const timeRangeParts = timeFilter.value?.split(' - ')
      if (timeRangeParts?.length === 2) {
        const startTimeStr = timeRangeParts[0].substring(0, 5) // "08:00"
        const endTimeStr = timeRangeParts[1].substring(0, 5) // "12:00"

        label += t('pages.marketingFlow.marketingFlowEditPage.trigger.timeRangeLabel', {
          startTime: startTimeStr,
          endTime: endTimeStr
        })
      }
    } else if (endTime.value && isSameDay(new Date(startDateFilter.value?.value), new Date(endTime.value))) {
      label += t('pages.marketingFlow.marketingFlowEditPage.trigger.timeRangeLabel', {
        startTime: new Date(startDateFilter.value?.value).toLocaleTimeString(locale.value, {
          hour: '2-digit',
          minute: '2-digit'
        }),
        endTime: new Date(endTime.value).toLocaleTimeString(locale.value, {
          hour: '2-digit',
          minute: '2-digit'
        })
      })
    }
  }

  if (isRepeated) {
    label += t('pages.marketingFlow.marketingFlowEditPage.trigger.startDateLabel', {
      startDate: new Date(startDateFilter.value?.value).toLocaleDateString(locale.value, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      })
    })
  }

  if (isRepeated && endDateTimeFilter?.value?.value) {
    label += t('pages.marketingFlow.marketingFlowEditPage.trigger.finalEndLabel', {
      finalEndDate: new Date(endDateTimeFilter.value.value).toLocaleDateString(locale.value, {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }),
      finalEndTime: new Date(endTime.value).toLocaleTimeString(locale.value, {
        hour: '2-digit',
        minute: '2-digit'
      })
    })
  }

  return label
})
</script>

<template>
  <MarketingFlowCard
    :disabled="disabledCardClick.trigger"
    type="trigger"
    :selected="currentStep === 'trigger'"
    :stats-count="formatStats(flowPerformance?.triggeredCustomers)"
    :is-stats-loaded="isStatsLoaded"
    :stats-label="t('pages.marketingFlow.marketingFlowEditPage.stats.contactsTriggered')"
    :error-status="triggerStepErrorStatus"
    @click="handleCardClick('trigger')"
  >
    <div v-if="triggerSettings" class="flex w-full flex-col">
      <div v-if="!triggerSettings.triggerType" class="w-full mt-3">
        <BaseChip color="yellow" :text="t('pages.marketingFlow.marketingFlowEditPage.trigger.triggerTypeToDefine')" />
      </div>

      <div v-if="triggerSettings.triggerType" class="mt-3 leading-6 inline-block text-sm text-foreground">
        <span v-if="triggerSettings.triggerType === 'SEGMENT_ENTRY'">
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.whenJoinSegment') }}
        </span>
        <span v-if="triggerSettings.triggerType === 'SEGMENT_EXIT'">
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.whenLeaveSegment') }}
        </span>
        <span v-if="triggerSettings.triggerType === 'BIRTHDAY'">
          <i18n-t
            keypath="pages.marketingFlow.marketingFlowEditPage.trigger.whenBirthday"
            tag="span"
            :plural="triggerSettings.dateRelativeOffsetDays ?? 0"
          >
            <template #days>
              <span class="font-semibold">{{ triggerSettings.dateRelativeOffsetDays ?? 0 }}</span>
            </template>
          </i18n-t>
        </span>
        <span v-if="triggerSettings.triggerType === 'CHECKIN'" v-html="labelCheckinFilterDate"></span>
        <span v-if="triggerSettings.triggerType === 'SIGNUP'">
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.whenSignup') }}
        </span>
        <span v-if="triggerSettings.triggerType === 'REWARD_RETRIEVAL'">
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.whenRewardRetrieval') }}
          <span v-for="(reward, index) in selectedTriggerRewards" :key="reward?.id">
            {{ ' ' }}
            <BaseChip :color="'gray'" :text="reward?.name" />
            <span v-if="index < selectedTriggerRewards.length - 1">
              {{ ' ' }}
              {{ t('global.words.or') }}
            </span>
          </span>
          <span v-if="selectedTriggerRewards.length === 0">
            <BaseChip :color="'yellow'" :text="t('pages.marketingFlow.marketingFlowEditPage.action.rewardToDefine')" />
          </span>
        </span>
        <span v-if="triggerSettings.triggerType === 'REWARD_NEAR_TO_UNLOCK'">
          {{
            t('pages.marketingFlow.marketingFlowEditPage.trigger.whenRewardNearToUnlockBeforePoint', {
              pointTreshold: triggerSettings.pointTreshold
            })
          }}
          {{ ' ' }}
          <BaseChip
            v-if="triggerSettings.pointTreshold"
            :color="isNumber(triggerSettings.pointTreshold) ? 'gray' : 'red'"
            :text="
              isNumber(triggerSettings.pointTreshold)
                ? t('global.words.pointsWithCount', { count: Number(triggerSettings.pointTreshold) })
                : t('pages.marketingFlow.marketingFlowEditPage.error.invalidNumberShort')
            "
          />
          <BaseChip
            v-else
            color="yellow"
            :text="t('pages.marketingFlow.marketingFlowEditPage.trigger.pointTresholdToDefine')"
          />
          {{ ' ' }}
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.whenRewardNearToUnlockAfterPoint') }}
          {{ ' ' }}
          <span v-for="(reward, index) in selectedTriggerRewards" :key="reward?.id">
            {{ ' ' }}
            <BaseChip
              :color="'gray'"
              :text="reward?.name + ` (${t('global.words.pointsWithCount', { count: reward?.points })})`"
            />
            <span v-if="index < selectedTriggerRewards.length - 1">
              {{ ' ' }}
              {{ t('global.words.or') }}
            </span>
          </span>
          <span v-if="selectedTriggerRewards.length === 0">
            <BaseChip :color="'yellow'" :text="t('pages.marketingFlow.marketingFlowEditPage.action.rewardToDefine')" />
          </span>
        </span>
        {{ ' ' }}
        <BaseChip
          v-if="triggerSettings.selectedAudience"
          :color="selectedAudience?.color"
          :icon="selectedAudience?.icon"
          :text="selectedAudience?.name"
        />
        <BaseChip
          v-if="templateAudienceName && !triggerSettings.selectedAudience"
          color="blue"
          :text="templateAudienceName"
        />
        <BaseChip
          v-if="
            !triggerSettings.selectedAudience &&
            ['SEGMENT_ENTRY', 'SEGMENT_EXIT'].includes(triggerSettings.triggerType) &&
            !templateAudienceName
          "
          color="yellow"
          :text="t('pages.marketingFlow.marketingFlowEditPage.trigger.segmentToDefine')"
        />
      </div>

      <div
        v-if="triggerSettings.triggerType"
        class="mt-3 flex flex-col items-start rounded-lg bg-muted p-3 text-xs gap-3"
      >
        <div
          v-if="triggerSettings.selectedStores?.length === 0"
          class="w-full rounded-md border bg-white px-3 py-1.5 text-xs leading-5"
        >
          <BaseChip
            class="my-1.5"
            color="yellow"
            :text="t('pages.marketingFlow.marketingFlowEditPage.trigger.storeToDefine')"
          />
        </div>

        <div
          v-if="triggerSettings.selectedStores?.length > 0"
          class="w-full rounded-md border bg-white px-3 py-1.5 text-xs leading-5"
        >
          {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.favoriteStores') }}
          <span v-if="selectedStores?.length === stores.length" class="font-semibold">
            {{ t('pages.autopilot.autopilotsPage.items.allStores') }}
          </span>
          <span v-else>
            <span v-for="(storeValue, index) in selectedStores" :key="storeValue?.id">
              <span class="font-semibold"> {{ storeValue.name }} </span
              >{{
                index === triggerSettings.selectedStores.length - 2
                  ? ` ${t('global.words.and')} `
                  : index === triggerSettings.selectedStores.length - 1
                    ? '.'
                    : ', '
              }}
            </span>
          </span>
        </div>

        <div
          v-if="triggerSettings.includedSegments?.length > 0"
          class="w-full rounded-md border bg-white px-3 py-1.5 text-xs leading-5"
        >
          {{
            t(
              'pages.marketingFlow.marketingFlowEditPage.trigger.includedSegmentsPreview',
              triggerSettings.includedSegments.length
            )
          }}
          <span>
            <span v-for="(segment, index) in triggerSettings.includedSegments" :key="segment.id">
              <span class="font-semibold"> {{ segment.name }} </span
              >{{
                index === triggerSettings.includedSegments.length - 2
                  ? ` ${t('global.words.or')} `
                  : index === triggerSettings.includedSegments.length - 1
                    ? '.'
                    : ', '
              }}
            </span>
          </span>
        </div>
        <div
          v-if="triggerSettings.excludedSegments?.length > 0"
          class="w-full rounded-md border bg-white px-3 py-1.5 text-xs leading-5"
        >
          {{
            t(
              'pages.marketingFlow.marketingFlowEditPage.trigger.excludedSegmentsPreview',
              triggerSettings.excludedSegments.length
            )
          }}
          <span>
            <span v-for="(segment, index) in triggerSettings.excludedSegments" :key="segment.id">
              <span class="font-semibold"> {{ segment.name }} </span
              >{{
                index === triggerSettings.excludedSegments.length - 2
                  ? ` ${t('global.words.or')} `
                  : index === triggerSettings.excludedSegments.length - 1
                    ? '.'
                    : ', '
              }}
            </span>
          </span>
        </div>

        <div
          v-if="triggerSettings.triggerType !== 'BIRTHDAY' && triggerSettings.multipleTimeScenario"
          class="w-full rounded-md border bg-white px-3 py-1.5 text-xs leading-5"
        >
          <span>
            {{ t('pages.marketingFlow.marketingFlowEditPage.trigger.canComeBackAfter') }}
          </span>
          <span v-if="triggerSettings.cooldownMinutes && triggerSettings.cooldownMinutes !== ''">
            <i18n-t keypath="pages.marketingFlow.marketingFlowEditPage.trigger.afterDaysDelay" tag="span">
              <template #dayCount>
                <span v-if="isNumber(triggerSettings.cooldownMinutes)" class="font-semibold">{{
                  t('pages.marketingFlow.marketingFlowEditPage.trigger.dayCount', {
                    count: Number(triggerSettings.cooldownMinutes) / 24 / 60
                  })
                }}</span>
                <span v-else class="font-semibold lowercase text-red-600">{{
                  t('pages.marketingFlow.marketingFlowEditPage.error.invalidNumberShort')
                }}</span>
              </template>
            </i18n-t>
          </span>
          <span v-if="triggerSettings.maxTriggersPerUser">
            <i18n-t keypath="pages.marketingFlow.marketingFlowEditPage.trigger.maxTimesNumber" tag="span">
              <template #timesCount>
                <span
                  v-if="isNumber(triggerSettings.maxTriggersPerUser)"
                  :class="[
                    'font-semibold',
                    isNumber(triggerSettings.maxTriggersPerUser) && Number(triggerSettings.maxTriggersPerUser) <= 1
                      ? 'text-red-600'
                      : ''
                  ]"
                  >{{
                    t('pages.marketingFlow.marketingFlowEditPage.trigger.timesCount', {
                      number: triggerSettings.maxTriggersPerUser
                    })
                  }}</span
                >
                <span v-else class="font-semibold lowercase text-red-600">{{
                  t('pages.marketingFlow.marketingFlowEditPage.error.invalidNumberShort')
                }}</span>
              </template>
            </i18n-t>
          </span>
        </div>
      </div>
    </div>
  </MarketingFlowCard>

  <div class="flex w-[425px] flex-row justify-center" :style="{ minHeight: '3rem' }">
    <div class="border-l-[2px] border-dashed" :style="{ height: '3rem' }"></div>
  </div>

  <MarketingFlowCard
    :disabled="disabledCardClick.delay"
    type="delay"
    :selected="currentStep === 'delay'"
    :stats-count="formatStats(flowPerformance?.delayedCustomers)"
    :is-stats-loaded="isStatsLoaded"
    :stats-label="t('pages.marketingFlow.marketingFlowEditPage.stats.contactsPending')"
    :error-status="delayStepErrorStatus"
    @click="handleCardClick('delay')"
  >
    <div
      v-if="
        delaySettings &&
        (isUsingTemplate || isTriggerStepCompleted(triggerSettings) || actionSettings.actions.length > 0)
      "
      class="flex w-full flex-col"
    >
      <div v-if="delaySettings.sendImmediatlyAfterTrigger" class="mt-3 leading-6 inline-block text-sm text-foreground">
        {{ t('pages.marketingFlow.marketingFlowEditPage.delay.nextStepNow') }}
      </div>

      <div v-else class="mt-3 leading-6 inline-block text-sm text-foreground">
        <span>{{ t('pages.marketingFlow.marketingFlowEditPage.delay.nextStepIn') }}</span>
        {{ ' ' }}
        <BaseChip
          v-if="
            delaySettings.daysBeforeAction !== null &&
            delaySettings.daysBeforeAction !== '' &&
            isNumber(delaySettings.daysBeforeAction)
          "
          :color="isNumber(delaySettings.daysBeforeAction) ? 'gray' : 'red'"
          :text="
            isNumber(delaySettings.daysBeforeAction)
              ? `${getDelayUnitLabel(delaySettings.delayUnit, Number(delaySettings.daysBeforeAction))}`
              : t('pages.marketingFlow.marketingFlowEditPage.error.invalidNumberShort')
          "
        />
        <BaseChip
          v-else
          :color="!delaySettings.daysBeforeAction ? 'yellow' : 'red'"
          :text="
            !delaySettings.daysBeforeAction
              ? t('pages.marketingFlow.marketingFlowEditPage.delay.daysBeforeActionToDefine')
              : t('pages.marketingFlow.marketingFlowEditPage.error.invalidNumberShort')
          "
        />
        {{ ' ' }}
        <span v-if="delaySettings.sendAtSpecificHour">
          <span>{{ t('global.words.dateAtTime') }}</span>
          {{ ' ' }}
          <BaseChip
            :color="delaySettings.hourToSend ? 'gray' : 'yellow'"
            :text="
              delaySettings.hourToSend
                ? delaySettings.hourToSendLabel
                : t('pages.marketingFlow.marketingFlowEditPage.delay.hourToSendToDefine')
            "
          />
        </span>
      </div>
    </div>
  </MarketingFlowCard>

  <div class="flex w-[425px] flex-row justify-center" :style="{ minHeight: '3rem' }">
    <div class="border-l-[2px] border-dashed" :style="{ height: '3rem' }"></div>
  </div>
  <MarketingFlowCard
    :disabled="disabledCardClick.action"
    type="action"
    :selected="currentStep === 'action'"
    :stats-count="formatStats(flowPerformance?.completedCustomers)"
    :is-stats-loaded="isStatsLoaded"
    :stats-label="t('pages.marketingFlow.marketingFlowEditPage.stats.contactsReceived')"
    :error-status="actionsStepErrorStatus"
    @click="handleCardClick('action')"
  >
    <div
      v-if="actionSettings.actions.length === 0 && !disabledCardClick.action && currentStep === 'action'"
      class="mt-3"
    >
      <BaseChip color="yellow" :text="t('pages.marketingFlow.marketingFlowEditPage.action.actionToDefine')" />
    </div>

    <div v-for="(action, index) in sortedActions" :key="'actionstep-' + index" class="flex w-full flex-col">
      <div v-if="action.actionType === 'SEND_SMS'" class="mt-3 leading-6 inline-block text-sm text-foreground">
        <span>{{ t('pages.marketingFlow.marketingFlowEditPage.action.soReceiveOne') }}</span>
        {{ ' ' }}
        <BaseChip
          color="blue"
          icon="HeroChatBubbleLeftIconOutline"
          :text="t('pages.marketingFlow.marketingFlowEditPage.action.sms')"
        />
        {{ ' ' }}
        <span v-if="action.preferPushChannel">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.preferPush') }}
        </span>
        {{ ' ' }}
        <span v-if="!isTransactionalTrigger" class="text-xs text-foreground-secondary leading-4!">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.ifCanReceive') }}
        </span>
      </div>
      <div v-if="action.actionType === 'SEND_PUSH'" class="mt-3 leading-6 inline-block text-sm text-foreground">
        <span>{{ t('pages.marketingFlow.marketingFlowEditPage.action.soReceiveOneFem') }}</span>
        {{ ' ' }}
        <BaseChip
          color="blue"
          icon="HeroBellAlertIconOutline"
          :text="t('pages.marketingFlow.marketingFlowEditPage.action.pushNotification')"
        />
        {{ ' ' }}
        <span v-if="action.preferSmsChannel">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.preferSms') }}
        </span>
        {{ ' ' }}
        <span v-if="!isTransactionalTrigger" class="text-xs text-foreground-secondary leading-4!">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.ifCanReceive') }}
        </span>
      </div>

      <div
        v-if="action.actionType === 'SEND_SMS' && action.messageContent"
        class="mt-3 flex flex-col items-start rounded-lg bg-muted p-3 text-xs"
      >
        <div class="flex flex-row items-center">
          <span>{{ t('global.words.from') }}: {{ loyaltyNotificationsConfigFields?.smsName?.value }}</span>
        </div>
        <div class="mt-2 flex flex-row items-center">
          <MessagePreview
            :message="
              getMarketingFlowPreviewText(
                action.messageContent,
                selectedStores?.[0]?.name ?? '',
                selectedReward,
                pointsAction?.bonusPoints
              )
            "
            :reward-name="selectedReward?.name"
            :custom-links="getCustomLinks(action.messageContent)"
            :show-stop="!isTransactionalTrigger"
          />
        </div>
      </div>

      <div
        v-if="action.actionType === 'SEND_PUSH' && action.messageContent"
        class="mt-3 flex flex-row items-center rounded-lg bg-muted p-3 text-xs"
      >
        <div class="mr-3 flex h-12 w-12 flex-row items-center rounded-full bg-border"></div>
        <div class="flex flex-1 flex-row items-center">
          <MessagePreview
            :message="
              getMarketingFlowPreviewText(
                action.messageContent,
                selectedStores?.[0]?.name ?? '',
                selectedReward,
                pointsAction?.bonusPoints
              )
            "
            :reward-name="selectedReward?.name"
            :custom-links="getCustomLinks(action.messageContent)"
          />
        </div>
      </div>

      <div v-if="['SEND_SMS', 'SEND_PUSH'].includes(action.actionType) && !action.messageContent" class="mt-3">
        <BaseChip color="yellow" :text="t('pages.marketingFlow.marketingFlowEditPage.action.messageToDefine')" />
      </div>

      <div v-if="action.actionType === 'SEND_EMAIL'" class="mt-3 leading-6 inline-block text-sm text-foreground">
        <span>{{ t('pages.marketingFlow.marketingFlowEditPage.action.soReceiveOne') }}</span>
        {{ ' ' }}
        <BaseChip
          color="blue"
          icon="HeroEnvelopeIconOutline"
          :text="t('pages.marketingFlow.marketingFlowEditPage.action.emailNotification')"
        />
      </div>

      <div v-if="action.actionType === 'CALL_EXTERNAL_API'" class="mt-3 text-sm text-foreground">
        <div class="rounded-lg border border-border bg-muted p-3">
          <div class="mb-2 text-xs text-foreground-secondary">
            {{ t('pages.marketingFlow.marketingFlowEditPage.action.soCallExternalApi') }}
          </div>

          <div class="flex flex-wrap items-center gap-1.5">
            <div :title="getExternalApiEndpointTitle(action)">
              <BaseChip color="blue" icon="HeroGlobeAltIconOutline" :text="getExternalApiEndpointLabel(action)" />
            </div>
            <BaseChip
              v-if="hasExternalApiAuth(action)"
              icon="HeroLockClosedIconOutline"
              :color="getExternalApiAuthChipColor(action)"
              :text="getExternalApiAuthSummary(action).typeLabel"
            />
          </div>

          <div
            v-if="getExternalApiAuthDetailChips(action).length"
            class="mt-2 flex flex-wrap items-center gap-1.5 text-xs"
          >
            <BaseChip
              v-for="detail in getExternalApiAuthDetailChips(action)"
              :key="detail"
              color="gray"
              :text="detail"
            />
          </div>
        </div>
      </div>

      <div v-if="action.actionType === 'CALL_EXTERNAL_API' && !action.externalApiUrl" class="mt-3">
        <BaseChip
          color="yellow"
          :text="t('pages.marketingFlow.marketingFlowEditPage.action.externalApi.urlToDefine')"
        />
      </div>

      <div v-if="action.actionType === 'SEND_EMAIL' && !action.selectedEmailTemplate" class="mt-3">
        <BaseChip color="yellow" :text="t('pages.marketingFlow.marketingFlowEditPage.action.emailToDefine')" />
      </div>

      <span v-if="action.actionType === 'REWARD'" class="mt-3 inline-block text-sm text-foreground">
        <span v-if="index === 0">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.rewardCard') }}
        </span>
        <span v-if="index > 0">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.rewardCardAnd') }}
        </span>
        {{ ' ' }}
        <BaseChip
          :color="selectedReward?.name ? 'gray' : 'yellow'"
          :text="selectedReward?.name ?? t('pages.marketingFlow.marketingFlowEditPage.action.rewardToDefine')"
        />
      </span>

      <span v-if="action.actionType === 'BONUS_POINTS'" class="mt-3 leading-6 inline-block text-sm text-foreground">
        <span v-if="index === 0">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusPointsCard') }}
        </span>
        <span v-if="index > 0">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusPointsCardAnd') }}
        </span>
        {{ ' ' }}

        <BaseChip
          v-if="pointsAction?.bonusPoints"
          :color="isNumber(pointsAction?.bonusPoints) ? 'gray' : 'red'"
          :text="
            isNumber(pointsAction?.bonusPoints) && Number(pointsAction?.bonusPoints) > 0
              ? t('global.words.pointsWithCount', { count: Number(pointsAction?.bonusPoints) })
              : t('pages.marketingFlow.marketingFlowEditPage.error.invalidNumberShort')
          "
        />
        <BaseChip
          v-else
          color="yellow"
          :text="t('pages.marketingFlow.marketingFlowEditPage.action.bonusPointsToDefine')"
        />
      </span>

      <span v-if="action.actionType === 'BONUS_MULTIPLIER'" class="mt-3 leading-6 inline-block text-sm text-foreground">
        <span v-if="index === 0">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusMultiplierCard') }}
        </span>
        <span v-if="index > 0">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusMultiplierCardAnd') }}
        </span>
        {{ ' ' }}
        <BaseChip :color="bonusMultiplierLabel.color" :text="bonusMultiplierLabel.label" />
        {{ ' ' }}
        <span v-if="triggerSettings.triggerType === 'CHECKIN'">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusCurrentOrder') }}
        </span>
        <span v-if="triggerSettings.triggerType !== 'CHECKIN'">
          {{ t('pages.marketingFlow.marketingFlowEditPage.action.bonusNextOrder') }}
        </span>
      </span>

      <div v-if="action.isTriggeredOnClick && CLICK_TRIGGERED_ACTION_TYPES.includes(action.actionType)" class="mt-2">
        <BaseChip color="blue" :text="t('pages.marketingFlow.marketingFlowEditPage.action.triggeredOnClickChip')" />
      </div>
    </div>

    <div v-if="hasRewardLinkWithoutReward && !isUsingTemplate" class="mt-3 flex items-center text-sm text-red-600">
      <BaseChip class="mx-1" color="red" :text="t('pages.marketingFlow.marketingFlowEditPage.action.rewardRequired')" />
    </div>
  </MarketingFlowCard>
</template>

<style>
@reference "@/input.css";

.label-html-content {
  strong {
    @apply !text-[12px] leading-[16px] !rounded-full !px-[8px] !py-[4px] !gap-1 !bg-muted !text-foreground;
  }
}

.error-tag {
  @apply !text-[12px] leading-[16px] !rounded-full !px-[8px] !py-[4px] !gap-1 !bg-red-100 !text-red-700;
}

.warning-tag {
  @apply !text-[12px] leading-[16px] !rounded-full !px-[8px] !py-[4px] !gap-1 !bg-yellow-100 !text-yellow-700;
}
</style>
